import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, IconButton, CircularProgress, Alert, TextField, Checkbox, FormControl, Select, MenuItem, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Image as ImageIcon, Add as AddIcon, Delete as DeleteIcon, Close as CloseIcon, ExpandMore as ExpandMoreIcon, DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getProduct, createProduct, updateProduct, ApiProduct, createPackage, createRequirement, CreatePackageRequest, CreateRequirementRequest, ApiRequirementTemplate, RequirementFieldType, getRequirementTemplates } from '../../api/productsService';
import { getSections, ApiSection } from '../../api/sectionsService';
import { vendorImageService } from '../../services/vendorImageService';
import { CustomTypography, CustomButton } from '../../components/shared';
import { DESIGN_TOKENS } from '../../styles/constants';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useVendorAuth } from '../../contexts/VendorAuthContext';

interface Requirement {
  id: string; // local UI id
  name: string; // backend requirement title
  fieldType: RequirementFieldType;
  checked: boolean;
  // Single text input (e.g. Min Order, Max Time in hours, Other)
  amount?: string;
  // Double-text input (e.g. Space Required: A x B)
  amountA?: string;
  amountB?: string;
  requirementId?: string; // Backend ID for updates
  iconURL?: string; // Icon URL for the requirement
  /** Default unit for text requirements (e.g. QAR, Hours), shown after input and sent to backend */
  defaultUnit?: string;
}

interface PackageOption {
  id: string;
  label: string;
  price?: string; // Optional for one-select and multi-select, stored as string for input
  locale?: {
    en?: { label?: string };
    ar?: { label?: string };
  };
}

export interface PackageItem {
  id: string;
  label: string;
  pricePerUnit: string;
  minUnits: string;
  maxUnits: string;
  locale?: {
    en?: { label?: string };
    ar?: { label?: string };
  };
}

interface Package {
  id: string;
  title: string;
  subTitle: string;
  type: 'one-select' | 'multi-select' | 'addons';
  required: boolean; // Whether the package is required or optional
  displayOrder: number; // Order in which packages should appear
  options?: PackageOption[];
  items?: PackageItem[];
  maxSelections?: string; // Stored as string for input, optional for multi-select
  maxCustomizableItems?: string; // Stored as string for input, optional for addons
  packageId?: string; // Backend ID for updates
  currentLang?: 'en' | 'ar'; // Current language for this package
  locale?: {
    en?: {
      title?: string;
      subTitle?: string;
    };
    ar?: {
      title?: string;
      subTitle?: string;
    };
  };
}

const ProductDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { vendor: sessionVendor } = useVendorAuth();

  const [product, setProduct] = useState<ApiProduct | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Image upload state
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [imageUploadProgress, setImageUploadProgress] = useState<string>('');

  // Form fields
  const [title, setTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [fullDescription, setFullDescription] = useState('');
  const [imageURLs, setImageURLs] = useState<string[]>([]);
  const [amount, setAmount] = useState<string>('');
  const [selectedSectionId, setSelectedSectionId] = useState<string>('');

  // Sections
  const [sections, setSections] = useState<ApiSection[]>([]);
  const [isLoadingSections, setIsLoadingSections] = useState(false);

  // Requirements - matching Figma design
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [requirementTemplates, setRequirementTemplates] = useState<ApiRequirementTemplate[]>([]);
  const [, setIsLoadingRequirementTemplates] = useState(false);
  const [iconErrors, setIconErrors] = useState<Set<string>>(new Set());

  // Packages - mock data for now
  const [packages, setPackages] = useState<Package[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedPackage, setExpandedPackage] = useState<string | false>(false);

  const isCreateMode = productId === 'new' || !productId;

  // Bilingual support
  const [currentLang, setCurrentLang] = useState<'en' | 'ar'>('en');
  const [locale, setLocale] = useState<{
    en?: { title?: string; shortDescription?: string; fullDescription?: string };
    ar?: { title?: string; shortDescription?: string; fullDescription?: string };
  }>({});

  // Reset requirements and packages when entering create mode
  useEffect(() => {
    if (isCreateMode) {
      setRequirements([]);
      setPackages([]);
      setTitle('');
      setShortDescription('');
      setFullDescription('');
      setImageURLs([]);
      setAmount('');
      setSelectedSectionId('');
      setPreviewUrls([]);
      setUploadedFiles([]);
      setError(null);
    }
  }, [isCreateMode]);

  usePageTitle(isCreateMode ? 'Add Product' : (product ? product.title : 'Product Details'));

  // Language change handler
  const handleLanguageChange = useCallback(
    (newLang: 'en' | 'ar') => {
      // 1. Save current visible values to locale[currentLang]
      const nextLocale = {
        ...locale,
        [currentLang]: {
          title: title.trim(),
          shortDescription: shortDescription.trim(),
          fullDescription: fullDescription.trim(),
        },
      };

      // 2. Update locale state
      setLocale(nextLocale);

      // 3. Switch language
      setCurrentLang(newLang);

      // 4. Load target language data
      const targetEntry = nextLocale[newLang] || {};
      setTitle(targetEntry.title ?? '');
      setShortDescription(targetEntry.shortDescription ?? '');
      setFullDescription(targetEntry.fullDescription ?? '');
    },
    [currentLang, locale, title, shortDescription, fullDescription]
  );

  // Package language change handler
  const handlePackageLanguageChange = useCallback(
    (packageId: string, newLang: 'en' | 'ar') => {
      setPackages(packages.map(pkg => {
        if (pkg.id !== packageId) return pkg;

        const pkgCurrentLang = pkg.currentLang || 'en';
        
        // 1. Save current package data to locale
        const nextLocale = {
          ...pkg.locale,
          [pkgCurrentLang]: {
            title: pkg.title.trim(),
            subTitle: pkg.subTitle.trim(),
          },
        };

        // 2. Save current options/items locale
        let updatedOptions = pkg.options;
        let updatedItems = pkg.items;

        if (pkg.options) {
          updatedOptions = pkg.options.map(opt => ({
            ...opt,
            locale: {
              ...opt.locale,
              [pkgCurrentLang]: {
                label: opt.label.trim(),
              },
            },
          }));
        }

        if (pkg.items) {
          updatedItems = pkg.items.map(item => ({
            ...item,
            locale: {
              ...item.locale,
              [pkgCurrentLang]: {
                label: item.label.trim(),
              },
            },
          }));
        }

        // 3. Load target language data
        const targetEntry = nextLocale[newLang] || {};
        const newTitle = targetEntry.title ?? '';
        const newSubTitle = targetEntry.subTitle ?? '';

        // 4. Load target language for options/items
        let finalOptions = updatedOptions;
        let finalItems = updatedItems;

        if (updatedOptions) {
          finalOptions = updatedOptions.map(opt => ({
            ...opt,
            label: opt.locale?.[newLang]?.label ?? '',
          }));
        }

        if (updatedItems) {
          finalItems = updatedItems.map(item => ({
            ...item,
            label: item.locale?.[newLang]?.label ?? '',
          }));
        }

        return {
          ...pkg,
          title: newTitle,
          subTitle: newSubTitle,
          currentLang: newLang,
          locale: nextLocale,
          options: finalOptions,
          items: finalItems,
        };
      }));
    },
    [packages]
  );

  // Build requirements UI state from templates + existing product data
  const buildRequirementsState = useCallback(
    (templates: ApiRequirementTemplate[], productRequirements?: ApiProduct['requirements']): Requirement[] => {
      return templates.map((tpl, index) => {
        const existing = productRequirements?.find((r) => r.name === tpl.title);

        const base: Requirement = {
          id: `${tpl.title}-${index}`,
          name: tpl.title,
          fieldType: tpl.fieldType,
          checked: !!existing,
          iconURL: tpl.iconURL,
          requirementId: existing?.requirementId,
          // Max Time unit is defined by the template (hours); ignore legacy stored "Minutes".
          defaultUnit:
            tpl.title === 'Max Time'
              ? (tpl.defaultUnit ?? existing?.unit)
              : (existing?.unit ?? tpl.defaultUnit),
        };

        if (!existing) {
          if (tpl.fieldType === 'double-text') {
            return { ...base, amountA: '', amountB: '' };
          }
          return base;
        }

        switch (tpl.fieldType) {
          case 'text': {
            // Store value only in input; unit is shown as label from defaultUnit
            const amountStr = existing.amount?.toString() ?? '';
            return { ...base, amount: amountStr, checked: true };
          }
          case 'checkbox':
            return { ...base, checked: true };
          case 'double-text': {
            const amt = existing.amount?.toString() ?? '';
            const match = amt.match(/^(.+?)\s*x\s*(.+)$/i);
            const amountA = match ? match[1].trim() : '';
            const amountB = match ? match[2].trim() : '';
            return { ...base, amountA, amountB, checked: !!(amountA && amountB) };
          }
          default:
            return base;
        }
      });
    },
    []
  );

  // Fetch requirement templates once
  useEffect(() => {
    const fetchTemplates = async () => {
      setIsLoadingRequirementTemplates(true);
      try {
        const templates = await getRequirementTemplates();
        setRequirementTemplates(templates);
      } catch (err) {
        console.error('Failed to load requirement templates', err);
        toast.error('Unable to load product requirements. Please try again later.');
      } finally {
        setIsLoadingRequirementTemplates(false);
      }
    };

    fetchTemplates();
  }, []);

  // Image upload dropzone
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const totalFiles = uploadedFiles.length + acceptedFiles.length;
    if (totalFiles > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }
    
    const newFiles = [...uploadedFiles, ...acceptedFiles];
    setUploadedFiles(newFiles);
    
    const newPreviewUrls = acceptedFiles.map(file => URL.createObjectURL(file));
    setPreviewUrls([...previewUrls, ...newPreviewUrls]);
  }, [uploadedFiles, previewUrls]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 10,
    multiple: true
  });

  const removeUploadedFile = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    const newPreviewUrls = previewUrls.filter((_, i) => i !== index);
    
    // Revoke the URL for the removed file
    URL.revokeObjectURL(previewUrls[index]);
    
    setUploadedFiles(newFiles);
    setPreviewUrls(newPreviewUrls);
  };

  const removeExistingImage = (index: number) => {
    const newImageURLs = imageURLs.filter((_, i) => i !== index);
    setImageURLs(newImageURLs);
  };

  useEffect(() => {
    const fetchProduct = async () => {
      // If create mode, skip fetching
      if (isCreateMode) {
        setIsLoading(false);
        return;
      }

      if (!productId) {
        setError('Product ID not found');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const fetchedProduct = await getProduct(productId);
        setProduct(fetchedProduct);
        
        // Load locale data if available
        if (fetchedProduct.locale) {
          setLocale(fetchedProduct.locale);
          // Load English data by default
          const enData = fetchedProduct.locale.en;
          if (enData) {
            setTitle(enData.title || fetchedProduct.title || '');
            setShortDescription(enData.shortDescription || fetchedProduct.shortDescription || '');
            setFullDescription(enData.fullDescription || fetchedProduct.fullDescription || '');
          } else {
            setTitle(fetchedProduct.title || '');
            setShortDescription(fetchedProduct.shortDescription || '');
            setFullDescription(fetchedProduct.fullDescription || '');
          }
        } else {
          setTitle(fetchedProduct.title || '');
          setShortDescription(fetchedProduct.shortDescription || '');
          setFullDescription(fetchedProduct.fullDescription || '');
        }
        
        setImageURLs(fetchedProduct.imageURLs || []);
        setAmount(fetchedProduct.amount ? String(fetchedProduct.amount) : '');
        setSelectedSectionId(fetchedProduct.sectionId || '');

        // Load packages and requirements from product data
        if (fetchedProduct.packages && Array.isArray(fetchedProduct.packages)) {
          const loadedPackages = fetchedProduct.packages.map((pkg: any, index: number) => {
            const packageId = String(index + 1);
            const loadedPkg: Package = {
              id: packageId,
              title: pkg.title || pkg.name || '',
              subTitle: pkg.subTitle || '',
              type: pkg.type || 'one-select',
              required: pkg.required !== undefined ? pkg.required : false,
              displayOrder: pkg.displayOrder ?? (index + 1),
              packageId: pkg.packageId, // Store backend ID for updates
              currentLang: 'en', // Default to English
              locale: pkg.locale || {}, // Load locale if available
            };

            // Load options if present
            if (pkg.options && Array.isArray(pkg.options)) {
              loadedPkg.options = pkg.options.map((opt: any, optIndex: number) => ({
                id: opt.id || String(Date.now() + optIndex),
                label: opt.label || '',
                // Convert price to string, use empty string if undefined or null
                price: opt.price !== undefined && opt.price !== null ? String(opt.price) : '',
                locale: opt.locale || {}, // Load option locale if available
              }));
            }

            // Load items if present
            if (pkg.items && Array.isArray(pkg.items)) {
              loadedPkg.items = pkg.items.map((item: any, itemIndex: number) => ({
                id: item.id || String(Date.now() + itemIndex),
                label: item.label || '',
                pricePerUnit: String(item.pricePerUnit || ''),
                minUnits: String(item.minUnits || ''),
                maxUnits: String(item.maxUnits || ''),
                locale: item.locale || {}, // Load item locale if available
              }));
            }

            // Load maxSelections for multi-select
            if (pkg.maxSelections !== undefined) {
              loadedPkg.maxSelections = String(pkg.maxSelections);
            }

            // Load maxCustomizableItems for addons
            if (pkg.maxCustomizableItems !== undefined) {
              loadedPkg.maxCustomizableItems = String(pkg.maxCustomizableItems);
            }

            return loadedPkg;
          });
          setPackages(loadedPackages);
        }
      } catch (err) {
        let message = 'Unable to load product right now.';

        if (axios.isAxiosError(err)) {
          message = err.response?.data?.message ?? err.message ?? message;
        } else if (err instanceof Error) {
          message = err.message;
        }

        setError(message);
        setProduct(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [productId, isCreateMode]);

  // When templates and/or product change, rebuild requirements UI state
  useEffect(() => {
    if (requirementTemplates.length === 0) {
      return;
    }
    setRequirements(buildRequirementsState(requirementTemplates, product?.requirements));
  }, [requirementTemplates, product, buildRequirementsState]);

  // Fetch sections
  useEffect(() => {
    const fetchSections = async () => {
      if (!sessionVendor?.vendorId) return;

      setIsLoadingSections(true);
      try {
        const response = await getSections(sessionVendor.vendorId);
        if (response.success) {
          setSections(response.sections);
          // If in create mode and no section is selected, check URL parameter first
          if (isCreateMode && response.sections.length > 0 && !selectedSectionId) {
            const sectionIdFromUrl = searchParams.get('sectionId');
            // Use section from URL if provided and valid, otherwise use first section
            if (sectionIdFromUrl && response.sections.some(s => s.sectionId === sectionIdFromUrl)) {
              setSelectedSectionId(sectionIdFromUrl);
            } else {
              setSelectedSectionId(response.sections[0].sectionId);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching sections:', err);
      } finally {
        setIsLoadingSections(false);
      }
    };

    fetchSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionVendor?.vendorId, isCreateMode]);

  // Cleanup blob URLs when component unmounts to prevent memory leaks
  useEffect(() => {
    return () => {
      // Revoke all blob URLs when component unmounts
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const handleBackClick = () => {
    navigate('/products');
  };


  const handleRequirementToggle = (id: string) => {
    setRequirements((prev) =>
      prev.map((req) =>
        req.id === id ? { ...req, checked: !req.checked } : req
      )
    );
  };

  const handleTextRequirementChange = (id: string, value: string) => {
    setRequirements((prev) =>
      prev.map((req) => {
        if (req.id === id && req.fieldType === 'text') {
          const amount = value;
          const checked = amount.trim().length > 0;
          return { ...req, amount, checked };
        }
        return req;
      })
    );
  };

  const handleDoubleTextRequirementChange = (id: string, part: 'A' | 'B', value: string) => {
    setRequirements((prev) =>
      prev.map((req) => {
        if (req.id === id && req.fieldType === 'double-text') {
          const amountA = part === 'A' ? value : (req.amountA ?? '');
          const amountB = part === 'B' ? value : (req.amountB ?? '');
          const checked = amountA.trim() !== '' && amountB.trim() !== '';
          return { ...req, amountA, amountB, checked };
        }
        return req;
      })
    );
  };

  const handlePackageChange = (id: string, field: keyof Package, value: string | boolean) => {
    setPackages(packages.map(pkg => 
      pkg.id === id ? { ...pkg, [field]: value } : pkg
    ));
  };

  const handlePackageRequiredToggle = (id: string) => {
    setPackages(packages.map(pkg => 
      pkg.id === id ? { ...pkg, required: !pkg.required } : pkg
    ));
  };

  const handlePackageTypeChange = (id: string, newType: 'one-select' | 'multi-select' | 'addons') => {
    setPackages(packages.map(pkg => {
      if (pkg.id === id) {
        // Reset type-specific fields when type changes
        const updated: Package = {
          ...pkg,
          type: newType,
        };
        
        if (newType === 'one-select' || newType === 'multi-select') {
          // Initialize options if not present
          if (!updated.options || updated.options.length === 0) {
            updated.options = [{ id: String(Date.now()), label: '', price: '', locale: {} }];
          }
          // Clear items and maxCustomizableItems
          delete updated.items;
          delete updated.maxCustomizableItems;
        } else if (newType === 'addons') {
          // Initialize items if not present
          if (!updated.items || updated.items.length === 0) {
            updated.items = [{ 
              id: String(Date.now()), 
              label: '', 
              pricePerUnit: '', 
              minUnits: '', 
              maxUnits: '',
              locale: {}
            }];
          }
          // Clear options and maxSelections
          delete updated.options;
          delete updated.maxSelections;
        }
        
        return updated;
      }
      return pkg;
    }));
  };

  const handleAddPackage = () => {
    const newId = String(Date.now());
    const newDisplayOrder = packages.length + 1;
    
    setPackages([...packages, { 
      id: newId, 
      title: '',
      subTitle: '',
      type: 'one-select',
      required: false,
      displayOrder: newDisplayOrder,
      currentLang: 'en', // Initialize with English
      locale: {}, // Initialize empty locale object
      options: [{ id: String(Date.now() + 1), label: '', price: '', locale: {} }]
    }]);
    // Automatically expand the newly added package
    setExpandedPackage(newId);
  };

  const handlePackageDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(packages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Recalculate displayOrder for all packages
    const reorderedPackages = items.map((pkg, index) => ({
      ...pkg,
      displayOrder: index + 1
    }));

    setPackages(reorderedPackages);
  };

  const handleRemovePackage = (id: string) => {
    setPackages(packages.filter(pkg => pkg.id !== id));
    // If the removed package was expanded, reset expanded state
    if (expandedPackage === id) {
      setExpandedPackage(false);
    }
  };

  const handleAddOption = (packageId: string) => {
    setPackages(packages.map(pkg => {
      if (pkg.id === packageId) {
        const newOption: PackageOption = {
          id: String(Date.now()),
          label: '',
          // Price is optional for both one-select and multi-select
          price: '',
          locale: {} // Initialize empty locale object
        };
        return {
          ...pkg,
          options: [...(pkg.options || []), newOption]
        };
      }
      return pkg;
    }));
  };

  const handleRemoveOption = (packageId: string, optionId: string) => {
    setPackages(packages.map(pkg => {
      if (pkg.id === packageId && pkg.options) {
        return {
          ...pkg,
          options: pkg.options.filter(opt => opt.id !== optionId)
        };
      }
      return pkg;
    }));
  };

  const handleOptionChange = (packageId: string, optionId: string, field: 'label' | 'price', value: string) => {
    setPackages(packages.map(pkg => {
      if (pkg.id === packageId && pkg.options) {
        return {
          ...pkg,
          options: pkg.options.map(opt =>
            opt.id === optionId ? { ...opt, [field]: value } : opt
          )
        };
      }
      return pkg;
    }));
  };

  const handleAddItem = (packageId: string) => {
    setPackages(packages.map(pkg => {
      if (pkg.id === packageId) {
        const newItem: PackageItem = {
          id: String(Date.now()),
          label: '',
          pricePerUnit: '',
          minUnits: '',
          maxUnits: '',
          locale: {} // Initialize empty locale object
        };
        return {
          ...pkg,
          items: [...(pkg.items || []), newItem]
        };
      }
      return pkg;
    }));
  };

  const handleRemoveItem = (packageId: string, itemId: string) => {
    setPackages(packages.map(pkg => {
      if (pkg.id === packageId && pkg.items) {
        return {
          ...pkg,
          items: pkg.items.filter(item => item.id !== itemId)
        };
      }
      return pkg;
    }));
  };

  const handleItemChange = (packageId: string, itemId: string, field: keyof PackageItem, value: string) => {
    setPackages(packages.map(pkg => {
      if (pkg.id === packageId && pkg.items) {
        return {
          ...pkg,
          items: pkg.items.map(item =>
            item.id === itemId ? { ...item, [field]: value } : item
          )
        };
      }
      return pkg;
    }));
  };

  const handleSaveOrCreate = async () => {
    if (!sessionVendor?.vendorId) {
      setError('Vendor session not found');
      toast.error('Vendor session not found');
      return;
    }

    // Validate required fields
    const missingFields: string[] = [];
    
    if (!title.trim()) {
      missingFields.push('Product title');
    }
    if (!fullDescription.trim()) {
      missingFields.push('Full description');
    }
    if (isCreateMode && !selectedSectionId) {
      missingFields.push('Section');
    }
    if (allImages.length === 0 && imageURLs.length === 0) {
      missingFields.push('At least one product image');
    }

    if (missingFields.length > 0) {
      const errorMessage = `Please fill in the following required fields: ${missingFields.join(', ')}`;
      setError(errorMessage);
      toast.error(errorMessage);
      return;
    }

    // Validate numeric fields for negative values
    const invalidFields: string[] = [];
    
    // Check cost field
    if (amount !== '' && parseFloat(amount) < 0) {
      invalidFields.push('Cost');
    }

    // Check requirements
    requirements.forEach((req) => {
      if (req.fieldType === 'text' && req.amount && req.checked) {
        const numMatch = req.amount.match(/^-?\d+\.?\d*/);
        if (numMatch) {
          const num = parseFloat(numMatch[0]);
          if (!isNaN(num) && num < 0) {
            invalidFields.push(`Requirement: ${req.name}`);
          }
        }
      }
    });
    
    // Check packages
    packages.forEach((pkg, pkgIndex) => {
      // Check option prices
      if (pkg.options) {
        pkg.options.forEach((opt, optIndex) => {
          if (opt.price !== '' && opt.price !== undefined && parseFloat(opt.price) < 0) {
            invalidFields.push(`Package "${pkg.title || `#${pkgIndex + 1}`}" - Option price`);
          }
        });
      }
      
      // Check max selections
      if (pkg.maxSelections !== '' && pkg.maxSelections !== undefined && parseFloat(pkg.maxSelections) < 0) {
        invalidFields.push(`Package "${pkg.title || `#${pkgIndex + 1}`}" - Max selections`);
      }
      
      // Check addon items
      if (pkg.items) {
        pkg.items.forEach((item, itemIndex) => {
          if (item.pricePerUnit !== '' && parseFloat(item.pricePerUnit) < 0) {
            invalidFields.push(`Package "${pkg.title || `#${pkgIndex + 1}`}" - Item price per unit`);
          }
          if (item.minUnits !== '' && parseFloat(item.minUnits) < 0) {
            invalidFields.push(`Package "${pkg.title || `#${pkgIndex + 1}`}" - Min units`);
          }
          if (item.maxUnits !== '' && parseFloat(item.maxUnits) < 0) {
            invalidFields.push(`Package "${pkg.title || `#${pkgIndex + 1}`}" - Max units`);
          }
        });
      }
      
      // Check max customizable items
      if (pkg.maxCustomizableItems !== '' && pkg.maxCustomizableItems !== undefined && parseFloat(pkg.maxCustomizableItems) < 0) {
        invalidFields.push(`Package "${pkg.title || `#${pkgIndex + 1}`}" - Max customizable items`);
      }
    });

    if (invalidFields.length > 0) {
      const errorMessage = `Invalid values. Please correct the following fields: ${invalidFields.join(', ')}`;
      setError(errorMessage);
      toast.error(errorMessage);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    let finalImageURLs = [...imageURLs];

    try {
      // Step 1: Upload new images if any files were selected
      if (uploadedFiles.length > 0) {
        setImageUploadProgress(`Uploading ${uploadedFiles.length} image(s)...`);
        const uploadedUrls: string[] = [];
        
        try {
          // Upload all files sequentially to avoid overwhelming the server
          for (let i = 0; i < uploadedFiles.length; i++) {
            const file = uploadedFiles[i];
            setImageUploadProgress(`Uploading image ${i + 1} of ${uploadedFiles.length}...`);
            
            // Generate pre-signed upload URL
            const urlData = await vendorImageService.generateUploadUrl({
              vendorId: sessionVendor.vendorId,
              ...(!isCreateMode && productId ? { productId } : {}),
              fileType: file.type,
            });

            if (!urlData.success) {
              throw new Error('Failed to generate upload URL');
            }

            // Upload directly to S3
            await vendorImageService.uploadToS3(urlData.uploadUrl, file);
            uploadedUrls.push(urlData.imageUrl);
          }

          // Combine existing URLs with newly uploaded URLs
          finalImageURLs = [...imageURLs, ...uploadedUrls];
          setImageURLs(finalImageURLs);
          
          // Clear preview state to prevent duplication
          previewUrls.forEach(url => URL.revokeObjectURL(url));
          setPreviewUrls([]);
          setUploadedFiles([]);
          
          setImageUploadProgress('');
        } catch (uploadError: any) {
          const uploadErrorMessage = uploadError.response?.data?.error || 
                                      uploadError.response?.data?.message || 
                                      uploadError.message || 
                                      'Failed to upload image. Please try again.';
          toast.error(uploadErrorMessage);
          setError(uploadErrorMessage);
          setIsSubmitting(false);
          setImageUploadProgress('');
          return;
        }
      }

      // Step 2: Create or update product
      setImageUploadProgress('Saving product...');
      
      // Save current language data to locale
      const finalLocale = {
        ...locale,
        [currentLang]: {
          title: title.trim(),
          shortDescription: shortDescription.trim(),
          fullDescription: fullDescription.trim(),
        },
      };

      // Use English as default, fallback to current if English not available
      const defaultTitle = finalLocale.en?.title ?? (currentLang === 'en' ? title.trim() : title.trim());
      const defaultShortDescription = finalLocale.en?.shortDescription ?? (currentLang === 'en' ? shortDescription.trim() : shortDescription.trim());
      const defaultFullDescription = finalLocale.en?.fullDescription ?? (currentLang === 'en' ? fullDescription.trim() : fullDescription.trim());
      
      // Calculate minPrice from packages
      // For one-select: no price
      // For multi-select: minimum of option prices
      // For addons: minimum of item pricePerUnit
      let minPrice = 0;
      if (packages.length > 0) {
        const prices: number[] = [];
        packages.forEach(pkg => {
          if (pkg.type === 'multi-select' && pkg.options) {
            pkg.options.forEach(opt => {
              if (opt.price) {
                const price = parseFloat(opt.price) || 0;
                if (price > 0) prices.push(price);
              }
            });
          } else if (pkg.type === 'addons' && pkg.items) {
            pkg.items.forEach(item => {
              if (item.pricePerUnit) {
                const price = parseFloat(item.pricePerUnit) || 0;
                if (price > 0) prices.push(price);
              }
            });
          }
        });
        minPrice = prices.length > 0 ? Math.min(...prices) : 0;
      }

      const productData: Omit<Partial<ApiProduct>, 'packages' | 'requirements'> = {
        title: defaultTitle,
        shortDescription: defaultShortDescription,
        fullDescription: defaultFullDescription,
        imageURLs: finalImageURLs,
        minPrice: minPrice,
        amount: amount ? parseFloat(amount) : undefined,
        sectionId: selectedSectionId || undefined,
        orderInSection: '0', // TODO: Set appropriate order
        locale: finalLocale, // Add locale to product data
      };

      if (isCreateMode) {
        if (!sessionVendor?.vendorId) {
          throw new Error('Vendor ID is required');
        }
        
        // Step 1: Create product (without requirements/packages)
        const createdProduct = await createProduct(sessionVendor.vendorId, productData);
        const createdProductId = createdProduct.productId;

        // Step 2: Create packages in separate table
        if (packages.length > 0) {
          setImageUploadProgress('Creating packages...');
          for (const pkg of packages) {
            if (pkg.title.trim()) {
              // Save current language data before creating
              const pkgCurrentLang = pkg.currentLang || 'en';
              const currentLangData: any = {
                title: pkg.title.trim(),
              };
              if (pkg.subTitle !== undefined) {
                currentLangData.subTitle = pkg.subTitle.trim();
              }
              const finalPackageLocale = {
                ...pkg.locale,
                [pkgCurrentLang]: currentLangData,
              };

              const packageData: CreatePackageRequest = {
                title: finalPackageLocale.en?.title ?? pkg.title.trim(),
                type: pkg.type,
                required: pkg.required,
                displayOrder: pkg.displayOrder,
                locale: finalPackageLocale,
              };
              
              // Debug: Log the package data being sent
              console.log('Creating package with data:', JSON.stringify(packageData, null, 2));
              
              // Add subTitle if defined (even empty string to clear it)
              const subTitleValue = finalPackageLocale.en?.subTitle ?? pkg.subTitle?.trim();
              if (subTitleValue !== undefined) {
                packageData.subTitle = subTitleValue;
              }

              // Add type-specific fields
              if (pkg.type === 'one-select' || pkg.type === 'multi-select') {
                if (pkg.options && pkg.options.length > 0) {
                  packageData.options = pkg.options
                    .filter(opt => opt.label.trim())
                    .map(opt => {
                      // Save current language for option
                      const optLocale = {
                        ...opt.locale,
                        [pkgCurrentLang]: {
                          label: opt.label.trim(),
                        },
                      };

                      const optionData: { id: string; label: string; price?: number; locale?: any } = {
                        id: opt.id,
                        label: optLocale.en?.label ?? opt.label.trim(),
                        locale: optLocale,
                      };
                      // Include price for both one-select and multi-select if provided
                      if (opt.price && opt.price.trim()) {
                        const parsedPrice = parseFloat(opt.price.trim().replace(/,/g, ''));
                        if (!isNaN(parsedPrice)) {
                          optionData.price = parsedPrice;
                        }
                      }
                      return optionData;
                    });
                }
                if (pkg.type === 'multi-select' && pkg.maxSelections) {
                  const maxSelections = pkg.maxSelections.trim().replace(/,/g, '');
                  if (maxSelections) {
                    packageData.maxSelections = parseInt(maxSelections) || undefined;
                  }
                }
              } else if (pkg.type === 'addons') {
                if (pkg.items && pkg.items.length > 0) {
                  packageData.items = pkg.items
                    .filter(item => item.label.trim())
                    .map(item => {
                      // Save current language for item
                      const itemLocale = {
                        ...item.locale,
                        [pkgCurrentLang]: {
                          label: item.label.trim(),
                        },
                      };

                      return {
                        id: item.id,
                        label: itemLocale.en?.label ?? item.label.trim(),
                        pricePerUnit: parseFloat((item.pricePerUnit || '0').trim().replace(/,/g, '')) || 0,
                        minUnits: parseInt((item.minUnits || '0').trim().replace(/,/g, '')) || 0,
                        maxUnits: parseInt((item.maxUnits || '0').trim().replace(/,/g, '')) || 0,
                        locale: itemLocale,
                      };
                    });
                }
                if (pkg.maxCustomizableItems) {
                  const maxCustomizable = pkg.maxCustomizableItems.trim().replace(/,/g, '');
                  if (maxCustomizable) {
                    packageData.maxCustomizableItems = parseInt(maxCustomizable) || undefined;
                  }
                }
              }

              await createPackage(createdProductId, packageData);
            }
          }
        }

        // Step 3: Create requirements in separate table
        const checkedRequirements = requirements.filter((req) => req.checked);
        if (checkedRequirements.length > 0) {
          setImageUploadProgress('Creating requirements...');
          for (const req of checkedRequirements) {
            let amount: string | number = '';
            let unit: string | undefined;

            switch (req.fieldType) {
              case 'checkbox':
                // Presence-only flag
                amount = '1';
                break;
              case 'text': {
                const value = (req.amount ?? '').trim();
                if (!value) {
                  continue;
                }
                amount = value;
                unit = req.defaultUnit;
                break;
              }
              case 'double-text': {
                const a = (req.amountA ?? '').trim();
                const b = (req.amountB ?? '').trim();
                if (!a || !b) {
                  continue;
                }
                amount = `${a} x ${b}`;
                unit = req.defaultUnit;
                break;
              }
              default:
                continue;
            }

            const requirementData: CreateRequirementRequest = {
              name: req.name,
              required: req.checked,
              amount,
              ...(unit ? { unit } : {}),
            };
            await createRequirement(createdProductId, requirementData);
          }
        }
      } else if (productId) {
        // Build requirements payload from UI state
        setImageUploadProgress('Preparing requirements...');
        const checkedRequirements = requirements.filter((req) => req.checked);
        const requirementsPayload: CreateRequirementRequest[] = [];
        for (const req of checkedRequirements) {
          let amount: string | number = '';
          let unit: string | undefined;

          switch (req.fieldType) {
            case 'checkbox':
              amount = '1';
              break;
            case 'text': {
              const value = (req.amount ?? '').trim();
              if (!value) {
                continue;
              }
              amount = value;
              unit = req.defaultUnit;
              break;
            }
            case 'double-text': {
              const a = (req.amountA ?? '').trim();
              const b = (req.amountB ?? '').trim();
              if (!a || !b) {
                continue;
              }
              amount = `${a} x ${b}`;
              unit = req.defaultUnit;
              break;
            }
            default:
              continue;
          }

          requirementsPayload.push({
            name: req.name,
            required: req.checked,
            amount,
            ...(unit ? { unit } : {}),
          });
        }

        // Build packages payload from UI state
        setImageUploadProgress('Preparing packages...');
        const packagesPayload: CreatePackageRequest[] = [];
        for (const pkg of packages.filter(p => p.title.trim())) {
          const pkgCurrentLang = pkg.currentLang || 'en';
          const currentLangData: any = {
            title: pkg.title.trim(),
          };
          if (pkg.subTitle !== undefined) {
            currentLangData.subTitle = pkg.subTitle.trim();
          }
          const finalPackageLocale = {
            ...pkg.locale,
            [pkgCurrentLang]: currentLangData,
          };

          const packageData: CreatePackageRequest = {
            title: finalPackageLocale.en?.title ?? pkg.title.trim(),
            type: pkg.type,
            required: pkg.required,
            displayOrder: pkg.displayOrder,
            locale: finalPackageLocale,
          };

          const subTitleValue = finalPackageLocale.en?.subTitle ?? pkg.subTitle?.trim();
          if (subTitleValue !== undefined) {
            packageData.subTitle = subTitleValue;
          }

          if (pkg.type === 'one-select' || pkg.type === 'multi-select') {
            if (pkg.options && pkg.options.length > 0) {
              packageData.options = pkg.options
                .filter(opt => opt.label.trim())
                .map(opt => {
                  const optLocale = {
                    ...opt.locale,
                    [pkgCurrentLang]: { label: opt.label.trim() },
                  };
                  const optionData: { id: string; label: string; price?: number; locale?: any } = {
                    id: opt.id,
                    label: optLocale.en?.label ?? opt.label.trim(),
                    locale: optLocale,
                  };
                  if (opt.price && opt.price.trim()) {
                    const parsedPrice = parseFloat(opt.price.trim().replace(/,/g, ''));
                    if (!isNaN(parsedPrice)) {
                      optionData.price = parsedPrice;
                    }
                  }
                  return optionData;
                });
            }
            if (pkg.type === 'multi-select' && pkg.maxSelections) {
              const maxSelections = pkg.maxSelections.trim().replace(/,/g, '');
              packageData.maxSelections = maxSelections ? parseInt(maxSelections) || undefined : undefined;
            }
          } else if (pkg.type === 'addons') {
            if (pkg.items && pkg.items.length > 0) {
              packageData.items = pkg.items
                .filter(item => item.label.trim())
                .map(item => {
                  const itemLocale = {
                    ...item.locale,
                    [pkgCurrentLang]: { label: item.label.trim() },
                  };
                  return {
                    id: item.id,
                    label: itemLocale.en?.label ?? item.label.trim(),
                    pricePerUnit: parseFloat((item.pricePerUnit || '0').trim().replace(/,/g, '')) || 0,
                    minUnits: parseInt((item.minUnits || '0').trim().replace(/,/g, '')) || 0,
                    maxUnits: parseInt((item.maxUnits || '0').trim().replace(/,/g, '')) || 0,
                    locale: itemLocale,
                  };
                });
            }
            if (pkg.maxCustomizableItems) {
              const maxCustomizable = pkg.maxCustomizableItems.trim().replace(/,/g, '');
              if (maxCustomizable) {
                packageData.maxCustomizableItems = parseInt(maxCustomizable) || undefined;
              }
            }
          }

          packagesPayload.push(packageData);
        }

        // Send everything in a single PATCH — backend deletes & recreates requirements/packages atomically
        setImageUploadProgress('Saving product...');
        await updateProduct(productId, {
          ...productData,
          requirements: requirementsPayload,
          packages: packagesPayload,
        });

        previewUrls.forEach(url => URL.revokeObjectURL(url));
        setUploadedFiles([]);
        setPreviewUrls([]);
        setImageURLs(finalImageURLs);
      } else {
        throw new Error('Product ID not found');
      }

      setImageUploadProgress('');
      toast.success(isCreateMode ? 'Product created successfully!' : 'Product updated successfully!');
      
      // Navigate back to products list
      if (isCreateMode) {
        navigate('/products');
      }
    } catch (err) {
      let message = isCreateMode 
        ? 'Unable to create product right now.' 
        : 'Unable to save product right now.';

      if (axios.isAxiosError(err)) {
        message = err.response?.data?.message ?? err.message ?? message;
      } else if (err instanceof Error) {
        message = err.message;
      }

      setError(message);
      toast.error(message);
      setImageUploadProgress('');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          fontFamily: DESIGN_TOKENS.typography.fontFamily,
          backgroundColor: DESIGN_TOKENS.colors.background,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: DESIGN_TOKENS.colors.primary }} />
      </Box>
    );
  }

  if (error && !isCreateMode && !product) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          fontFamily: DESIGN_TOKENS.typography.fontFamily,
          backgroundColor: DESIGN_TOKENS.colors.background,
          px: 10,
          py: 5,
        }}
      >
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <CustomButton onClick={handleBackClick} variant="primary">
          Back to Products
        </CustomButton>
      </Box>
    );
  }

  // Combine existing images with preview URLs
  const allImages = [
    ...imageURLs.map(url => ({ url, type: 'existing' as const })),
    ...previewUrls.map(url => ({ url, type: 'preview' as const }))
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        fontFamily: DESIGN_TOKENS.typography.fontFamily,
        backgroundColor: DESIGN_TOKENS.colors.background,
      }}
    >
      <Box
        component="main"
        sx={{
          bgcolor: DESIGN_TOKENS.colors.background,
          p: 0,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Header with back button */}
        <Box sx={{ px: 10, pt: 5, pb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
              <IconButton
                onClick={handleBackClick}
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  bgcolor: 'transparent',
                  '&:hover': { bgcolor: 'rgba(103, 49, 79, 0.04)' },
                }}
              >
                <ArrowBackIcon sx={{ color: DESIGN_TOKENS.colors.primary, fontSize: 16 }} />
              </IconButton>
            <CustomTypography variant="h1">
              {isCreateMode ? 'Add Product' : product?.title || ''}
            </CustomTypography>
            </Box>
            {/* Save/Create Button - Top Right */}
            <CustomButton 
              variant="primary" 
              size="large" 
              onClick={handleSaveOrCreate}
              disabled={isSubmitting}
            >
              {isSubmitting ? (isCreateMode ? 'Creating...' : 'Saving...') : (isCreateMode ? 'Create' : 'Save changes')}
            </CustomButton>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ px: 10, pb: 5 }}>
          {error && isCreateMode && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-start' }}>
            {/* Left Column */}
            <Box sx={{ width: '495px', display: 'flex', flexDirection: 'column', gap: 5 }}>
              {/* Image Section */}
              <Box>
                <CustomTypography
                  variant="h3"
                  sx={{
                    mb: 2.5,
                    fontSize: '22px',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: DESIGN_TOKENS.colors.text,
                  }}
                >
                  Images {allImages.length > 0 && `(${allImages.length}/10)`}
                </CustomTypography>
                
                {/* Image Grid */}
                {allImages.length > 0 && (
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: 2, 
                    mb: 3 
                  }}>
                    {allImages.map((image, index) => (
                      <Box
                        key={index}
                        sx={{
                          position: 'relative',
                          width: '100%',
                          aspectRatio: '1',
                          borderRadius: DESIGN_TOKENS.spacing.borderRadius,
                          overflow: 'hidden',
                          backgroundColor: '#f1f4f4',
                        }}
                      >
                        <Box
                          component="img"
                          src={image.url}
                          alt={`Product image ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        {index === 0 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              backgroundColor: DESIGN_TOKENS.colors.primary,
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '10px',
                              fontWeight: 600,
                            }}
                          >
                            Primary
                          </Box>
                        )}
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (image.type === 'preview') {
                              const previewIndex = index - imageURLs.length;
                              removeUploadedFile(previewIndex);
                            } else {
                              removeExistingImage(index);
                            }
                          }}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Upload Dropzone */}
                {allImages.length < 10 && (
                  <Box
                    {...getRootProps()}
                    sx={{
                      width: '100%',
                      minHeight: allImages.length === 0 ? '333.37px' : '150px',
                      borderRadius: DESIGN_TOKENS.spacing.borderRadius,
                      overflow: 'hidden',
                      backgroundColor: isDragActive ? '#f8f9fa' : '#f1f4f4',
                      border: isDragActive ? `2px dashed ${DESIGN_TOKENS.colors.primary}` : '2px dashed transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      position: 'relative',
                      '&:hover': {
                        borderColor: DESIGN_TOKENS.colors.primary,
                        backgroundColor: '#f8f9fa',
                      },
                    }}
                  >
                    <input {...getInputProps()} />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                        textAlign: 'center',
                        padding: 3,
                      }}
                    >
                      <ImageIcon 
                        sx={{ 
                          fontSize: allImages.length === 0 ? 48 : 32, 
                          color: isDragActive ? DESIGN_TOKENS.colors.primary : '#93989c' 
                        }} 
                      />
                      <Typography
                        sx={{
                          fontSize: allImages.length === 0 ? 16 : 14,
                          fontWeight: 600,
                          color: DESIGN_TOKENS.colors.text,
                          fontFamily: DESIGN_TOKENS.typography.fontFamily,
                        }}
                      >
                        {isDragActive ? 'Drop your images here' : allImages.length === 0 ? 'Upload product images' : 'Add more images'}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: '#93989c',
                          fontFamily: DESIGN_TOKENS.typography.fontFamily,
                        }}
                      >
                        {allImages.length === 0 ? 'Drag and drop or click to browse' : `Up to ${10 - allImages.length} more`}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: '#93989c',
                          fontFamily: DESIGN_TOKENS.typography.fontFamily,
                        }}
                      >
                        PNG, JPG, GIF up to 10MB (Max 10 images)
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Upload Progress */}
                {imageUploadProgress && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: 'rgba(103, 49, 79, 0.1)',
                      borderRadius: 1,
                      fontSize: '14px',
                      textAlign: 'center',
                      color: DESIGN_TOKENS.colors.text,
                    }}
                  >
                    {imageUploadProgress}
                  </Box>
                )}
              </Box>

              {/* Section Selection - Only show in create mode */}
              {isCreateMode && (
                <Box>
                  <CustomTypography
                    variant="h3"
                    sx={{
                      mb: 2.5,
                      fontSize: '22px',
                      fontWeight: 700,
                      lineHeight: 1.2,
                      color: DESIGN_TOKENS.colors.text,
                    }}
                  >
                    Section
                  </CustomTypography>
                  <FormControl fullWidth>
                    <Select
                      value={selectedSectionId}
                      onChange={(e) => setSelectedSectionId(e.target.value)}
                      disabled={isLoadingSections}
                      displayEmpty
                      sx={{
                        backgroundColor: '#f6f8f8',
                        borderRadius: '12px',
                        height: '56px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#f6f8f8',
                        },
                        '&:hover': {
                          backgroundColor: '#f6f8f8',
                        },
                        '& .MuiSelect-select': {
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '16px',
                          color: selectedSectionId ? '#181818' : '#93989c',
                          padding: '16px 14px',
                          backgroundColor: 'transparent',
                        },
                        '& .MuiSelect-icon': {
                          color: '#93989c',
                        },
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#f6f8f8',
                          borderRadius: '12px',
                          height: '56px',
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          },
                          '&:hover': {
                            backgroundColor: '#f6f8f8',
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#f6f8f8',
                          }
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: '12px',
                            fontFamily: DESIGN_TOKENS.typography.fontFamily,
                            maxHeight: 300,
                            mt: 1,
                          }
                        }
                      }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: '#93989c' }}>Select Section</span>;
                        }
                        const section = sections.find(s => s.sectionId === selected);
                        return section ? section.name : selected;
                      }}
                    >
                      {sections.map((section) => (
                        <MenuItem 
                          key={section.sectionId} 
                          value={section.sectionId}
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '16px',
                          }}
                        >
                          {section.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Cost Section */}
              <Box>
                <CustomTypography
                  variant="h3"
                  sx={{
                    mb: 2.5,
                    fontSize: '22px',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: DESIGN_TOKENS.colors.text,
                  }}
                >
                  Cost
                </CustomTypography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="Amount (cost)"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  error={amount !== '' && parseFloat(amount) < 0}
                  helperText={amount !== '' && parseFloat(amount) < 0 ? 'Must be 0 or greater' : ''}
                  inputProps={{
                    step: '0.01',
                    min: '0'
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f6f8f8',
                      borderRadius: '12px',
                      height: '56px',
                      '& fieldset': {
                        border: 'none'
                      },
                      '&:hover fieldset': {
                        border: 'none'
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none'
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '16px',
                      color: amount ? '#181818' : '#93989c',
                      '&::placeholder': {
                        color: '#93989c',
                        opacity: 1
                      }
                    }
                  }}
                />
              </Box>

              {/* Language Selector */}
              <Box>
                <CustomTypography
                  variant="h3"
                  sx={{
                    mb: 2.5,
                    fontSize: '22px',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: DESIGN_TOKENS.colors.text,
                  }}
                >
                  Content Language
                </CustomTypography>
                <FormControl fullWidth>
                  <Select
                    value={currentLang}
                    onChange={(e) => handleLanguageChange(e.target.value as 'en' | 'ar')}
                    displayEmpty
                    disabled={isSubmitting}
                    sx={{
                      backgroundColor: '#f6f8f8',
                      borderRadius: '12px',
                      height: '56px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#f6f8f8',
                      },
                      '&:hover': {
                        backgroundColor: '#f6f8f8',
                      },
                      '& .MuiSelect-select': {
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '16px',
                        color: '#181818',
                        padding: '16px 14px',
                        backgroundColor: 'transparent',
                      },
                      '& .MuiSelect-icon': {
                        color: '#93989c',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '12px',
                          fontFamily: DESIGN_TOKENS.typography.fontFamily,
                          maxHeight: 300,
                          mt: 1,
                        }
                      }
                    }}
                  >
                    <MenuItem value="en" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '16px' }}>
                      English
                    </MenuItem>
                    <MenuItem value="ar" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '16px' }}>
                      Arabic
                    </MenuItem>
                  </Select>
                </FormControl>
                <CustomTypography
                  variant="caption"
                  sx={{
                    mt: 1,
                    display: 'block',
                    color: '#93989c',
                    fontSize: '14px',
                  }}
                >
                </CustomTypography>
              </Box>

              {/* Description Section */}
              <Box>
                <CustomTypography
                  variant="h3"
                  sx={{
                    mb: 2.5,
                    fontSize: '22px',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: DESIGN_TOKENS.colors.text,
                  }}
                >
                  Description
                </CustomTypography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Product title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f6f8f8',
                        borderRadius: '12px',
                        height: '56px',
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '16px',
                        color: title ? '#181818' : '#93989c',
                        '&::placeholder': {
                          color: '#93989c',
                          opacity: 1
                        }
                      }
                    }}
                  />
                  <TextField
                    fullWidth
                    placeholder="Short description"
                    value={shortDescription}
                    onChange={(e) => setShortDescription(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f6f8f8',
                        borderRadius: '12px',
                        height: '56px',
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '16px',
                        color: shortDescription ? '#181818' : '#93989c',
                        '&::placeholder': {
                          color: '#93989c',
                          opacity: 1
                        }
                      }
                    }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Full description"
                    value={fullDescription}
                    onChange={(e) => setFullDescription(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f6f8f8',
                        borderRadius: '12px',
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '16px',
                        color: fullDescription ? '#181818' : '#93989c',
                        '&::placeholder': {
                          color: '#93989c',
                          opacity: 1
                        }
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* Requirements Section */}
              <Box>
                <CustomTypography
                  variant="h3"
                  sx={{
                    mb: 2.5,
                    fontSize: '22px',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: DESIGN_TOKENS.colors.text,
                  }}
                >
                  Requirements
                </CustomTypography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {requirements.map((requirement) => (
                    <Box
                      key={requirement.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '60px',
                        gap: 2,
                        width: '100%',
                      }}
                    >
                      {/* Checkbox */}
                      <Checkbox
                        checked={requirement.checked}
                        onChange={() => handleRequirementToggle(requirement.id)}
                        sx={{
                          color: DESIGN_TOKENS.colors.primary,
                          padding: 0,
                          width: '24px',
                          height: '24px',
                          '&.Mui-checked': {
                            color: DESIGN_TOKENS.colors.primary,
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: '24px',
                          },
                        }}
                      />
                      
                      {/* Requirement Title */}
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '16px',
                          fontWeight: 400,
                          lineHeight: 1.4,
                          color: DESIGN_TOKENS.colors.text,
                          minWidth: '140px',
                          flexShrink: 0,
                        }}
                      >
                        {requirement.name}
                      </Typography>
                      
                      {/* Inputs based on field type - fixed width for equal row spacing */}
                      {requirement.fieldType === 'text' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '280px', flexShrink: 0 }}>
                          <TextField
                            fullWidth
                            placeholder="Value"
                            value={requirement.amount ?? ''}
                            onChange={(e) => handleTextRequirementChange(requirement.id, e.target.value)}
                            sx={{
                              flex: 1,
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#f6f8f8',
                                borderRadius: '12px',
                                height: '56px',
                                '& fieldset': {
                                  border: 'none'
                                },
                                '&:hover fieldset': {
                                  border: 'none'
                                },
                                '&.Mui-focused fieldset': {
                                  border: 'none'
                                }
                              },
                              '& .MuiInputBase-input': {
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '16px',
                                color: requirement.amount ? '#181818' : '#93989c',
                                padding: '16px 14px',
                                '&::placeholder': {
                                  color: '#93989c',
                                  opacity: 1
                                }
                              }
                            }}
                          />
                          {requirement.defaultUnit && (
                            <Typography
                              sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '14px',
                                color: DESIGN_TOKENS.colors.text,
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                              }}
                            >
                              {requirement.defaultUnit}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {requirement.fieldType === 'double-text' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '280px', flexShrink: 0 }}>
                          <TextField
                            placeholder="A"
                            value={requirement.amountA ?? ''}
                            onChange={(e) => handleDoubleTextRequirementChange(requirement.id, 'A', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#f6f8f8',
                                borderRadius: '12px',
                                height: '56px',
                                '& fieldset': {
                                  border: 'none'
                                }
                              },
                              '& .MuiInputBase-input': {
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '16px',
                                padding: '16px 14px',
                              }
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: '"Inter", sans-serif',
                              fontSize: '16px',
                              color: DESIGN_TOKENS.colors.text,
                            }}
                          >
                            x
                          </Typography>
                          <TextField
                            placeholder="B"
                            value={requirement.amountB ?? ''}
                            onChange={(e) => handleDoubleTextRequirementChange(requirement.id, 'B', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#f6f8f8',
                                borderRadius: '12px',
                                height: '56px',
                                '& fieldset': {
                                  border: 'none'
                                }
                              },
                              '& .MuiInputBase-input': {
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '16px',
                                padding: '16px 14px',
                              }
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: '"Inter", sans-serif',
                              fontSize: '14px',
                              color: DESIGN_TOKENS.colors.text,
                              ml: 1,
                            }}
                          >
                            {requirement.defaultUnit}
                          </Typography>
                        </Box>
                      )}

                      {requirement.fieldType === 'checkbox' && (
                        <Box sx={{ width: '280px', flexShrink: 0 }} />
                      )}
                      
                      {/* Icon - always same width for equal spacing; use placeholder when no icon */}
                      <Box
                        sx={{
                          width: '24px',
                          height: '24px',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {requirement.iconURL && !iconErrors.has(requirement.id) ? (
                          <Box
                            component="img"
                            src={requirement.iconURL}
                            alt={requirement.name}
                            onError={() => {
                              setIconErrors(prev => new Set(prev).add(requirement.id));
                            }}
                            sx={{
                              width: '24px',
                              height: '24px',
                              objectFit: 'contain',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                              backgroundColor: '#e0e0e0',
                            }}
                            aria-hidden
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Right Column */}
            <Box sx={{ width: '495px', display: 'flex', flexDirection: 'column', gap: 5 }}>
              {/* Packages and Prices Section */}
              <Box>
                <CustomTypography
                  variant="h3"
                  sx={{
                    mb: 2.5,
                    fontSize: '22px',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: DESIGN_TOKENS.colors.text,
                  }}
                >
                  Packages and prices
                </CustomTypography>
                <DragDropContext onDragEnd={handlePackageDragEnd}>
                  <Droppable droppableId="packages">
                    {(provided) => (
                      <Box
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                      >
                        {packages.map((pkg, index) => (
                          <Draggable key={pkg.id} draggableId={pkg.id} index={index}>
                            {(provided, snapshot) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  ...provided.draggableProps.style,
                                  opacity: snapshot.isDragging ? 0.8 : 1,
                                }}
                              >
                                <Accordion
                                  key={pkg.id}
                                  expanded={expandedPackage === pkg.id}
                                  onChange={(event, isExpanded) => {
                                    setExpandedPackage(isExpanded ? pkg.id : false);
                                  }}
                                  sx={{
                                    width: '100%',
                                    position: 'relative',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '12px !important',
                                    backgroundColor: '#fafafa',
                                    boxShadow: 'none',
                                    '&:before': {
                                      display: 'none',
                                    },
                                    '&.Mui-expanded': {
                                      margin: 0,
                                    },
                                  }}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: DESIGN_TOKENS.colors.text }} />}
                                    sx={{
                                      minHeight: '72px',
                                      '&.Mui-expanded': {
                                        minHeight: '72px',
                                      },
                                      '& .MuiAccordionSummary-content': {
                                        margin: '16px 0',
                                        '&.Mui-expanded': {
                                          margin: '16px 0',
                                        },
                                      },
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 5 }}>
                                      <Box {...provided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <DragIndicatorIcon sx={{ cursor: 'grab', color: 'text.secondary', fontSize: 20 }} />
                                      </Box>
                                      <Typography
                                        sx={{
                                          fontFamily: '"Inter", sans-serif',
                                          fontSize: '18px',
                                          fontWeight: 600,
                                          color: pkg.title ? DESIGN_TOKENS.colors.text : '#93989c',
                                          flex: 1,
                                        }}
                                      >
                                        {pkg.title || 'Untitled Package'}
                                      </Typography>
                                      <IconButton
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemovePackage(pkg.id);
                                        }}
                                        sx={{
                                          width: '32px',
                                          height: '32px',
                                          borderRadius: '8px',
                                          bgcolor: DESIGN_TOKENS.colors.primary,
                                          color: '#ffffff',
                                          '&:hover': { 
                                            bgcolor: DESIGN_TOKENS.colors.primaryDark,
                                          },
                                        }}
                                      >
                                        <DeleteIcon sx={{ fontSize: 20 }} />
                                      </IconButton>
                                    </Box>
                                  </AccordionSummary>
                      <AccordionDetails sx={{ p: 3, pt: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Title */}
                          <TextField
                            fullWidth
                            placeholder="Package title *"
                            value={pkg.title}
                            onChange={(e) => handlePackageChange(pkg.id, 'title', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#f6f8f8',
                              borderRadius: '12px',
                              height: '56px',
                              '& fieldset': {
                                border: 'none'
                              },
                              '&:hover fieldset': {
                                border: 'none'
                              },
                              '&.Mui-focused fieldset': {
                                border: 'none'
                              }
                            },
                            '& .MuiInputBase-input': {
                              fontFamily: '"Inter", sans-serif',
                              fontSize: '16px',
                            color: pkg.title ? '#181818' : '#93989c',
                              '&::placeholder': {
                                color: '#93989c',
                                opacity: 1
                              }
                            }
                          }}
                        />

                      {/* Sub-title */}
                        <TextField
                          fullWidth
                        placeholder="Sub-title (optional)"
                        value={pkg.subTitle || ''}
                        onChange={(e) => handlePackageChange(pkg.id, 'subTitle', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#f6f8f8',
                              borderRadius: '12px',
                              height: '56px',
                              '& fieldset': {
                                border: 'none'
                              },
                              '&:hover fieldset': {
                                border: 'none'
                              },
                              '&.Mui-focused fieldset': {
                                border: 'none'
                              }
                            },
                            '& .MuiInputBase-input': {
                              fontFamily: '"Inter", sans-serif',
                              fontSize: '16px',
                            color: pkg.subTitle ? '#181818' : '#93989c',
                              '&::placeholder': {
                                color: '#93989c',
                                opacity: 1
                              }
                            }
                          }}
                        />

                      {/* Package Language Selector */}
                      <Box>
                        <CustomTypography
                          variant="caption"
                          sx={{
                            mb: 0.5,
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: DESIGN_TOKENS.colors.text,
                          }}
                        >
                          Package Language
                        </CustomTypography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={pkg.currentLang || 'en'}
                            onChange={(e) => handlePackageLanguageChange(pkg.id, e.target.value as 'en' | 'ar')}
                            disabled={isSubmitting}
                            sx={{
                              backgroundColor: '#f6f8f8',
                              borderRadius: '8px',
                              height: '40px',
                              '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: 'none'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: 'none'
                              },
                              '& .MuiSelect-select': {
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '14px',
                                color: '#181818',
                                padding: '8px 12px',
                              },
                              '& .MuiSelect-icon': {
                                color: '#93989c',
                              },
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  borderRadius: '8px',
                                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                                  maxHeight: 200,
                                  mt: 0.5,
                                }
                              }
                            }}
                          >
                            <MenuItem value="en" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '14px' }}>
                              English
                            </MenuItem>
                            <MenuItem value="ar" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '14px' }}>
                              Arabic
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <CustomTypography
                          variant="caption"
                          sx={{
                            mt: 0.5,
                            display: 'block',
                            color: '#93989c',
                            fontSize: '12px',
                          }}
                        >
                        </CustomTypography>
                      </Box>

                      {/* Type Selector and Required Checkbox */}
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <FormControl fullWidth>
                          <Select
                            value={pkg.type}
                            onChange={(e) => handlePackageTypeChange(pkg.id, e.target.value as 'one-select' | 'multi-select' | 'addons')}
                            sx={{
                              backgroundColor: '#f6f8f8',
                              borderRadius: '12px',
                              height: '56px',
                              '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: 'none'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: 'none'
                              },
                              '& .MuiSelect-select': {
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '16px',
                                color: '#181818',
                                padding: '16px 14px',
                              },
                            }}
                          >
                            <MenuItem value="one-select">One Select</MenuItem>
                            <MenuItem value="multi-select">Multi-Select</MenuItem>
                            <MenuItem value="addons">Ad-ons</MenuItem>
                          </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
                          <Checkbox
                            checked={pkg.required}
                            onChange={() => handlePackageRequiredToggle(pkg.id)}
                            sx={{
                              color: DESIGN_TOKENS.colors.primary,
                              padding: 0,
                              width: '24px',
                              height: '24px',
                              '&.Mui-checked': {
                                color: DESIGN_TOKENS.colors.primary,
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: '24px',
                              },
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: '"Inter", sans-serif',
                              fontSize: '16px',
                              fontWeight: 400,
                              color: DESIGN_TOKENS.colors.text,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Required
                          </Typography>
                        </Box>
                      </Box>

                      {/* One-select and Multi-select Options */}
                      {(pkg.type === 'one-select' || pkg.type === 'multi-select') && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <CustomTypography variant="body1" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.text }}>
                            Options:
                          </CustomTypography>
                          {pkg.options?.map((opt, optIndex) => (
                            <Box key={opt.id} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                          fullWidth
                                placeholder="Option label"
                                value={opt.label}
                                onChange={(e) => handleOptionChange(pkg.id, opt.id, 'label', e.target.value)}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#ffffff',
                                    borderRadius: '12px',
                                    height: '56px',
                                    '& fieldset': {
                                      border: '1px solid #e0e0e0'
                                    }
                                  },
                                  '& .MuiInputBase-input': {
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '16px',
                                  }
                                }}
                              />
                              <TextField
                                type="number"
                                placeholder="Price (optional)"
                                value={opt.price || ''}
                                onChange={(e) => handleOptionChange(pkg.id, opt.id, 'price', e.target.value)}
                                error={opt.price !== '' && opt.price !== undefined && parseFloat(opt.price) < 0}
                                helperText={opt.price !== '' && opt.price !== undefined && parseFloat(opt.price) < 0 ? 'Must be 0 or greater' : ''}
                                sx={{
                                  width: '150px',
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#ffffff',
                                    borderRadius: '12px',
                                    height: '56px',
                                    '& fieldset': {
                                      border: '1px solid #e0e0e0'
                                    }
                                  },
                                  '& .MuiInputBase-input': {
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '16px',
                                  }
                                }}
                              />
                              {pkg.options && pkg.options.length > 1 && (
                                <IconButton
                                  onClick={() => handleRemoveOption(pkg.id, opt.id)}
                                  sx={{
                                    width: '40px',
                                    height: '40px',
                                    color: DESIGN_TOKENS.colors.primary,
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                          <Box sx={{ alignSelf: 'flex-start' }}>
                            <CustomButton
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddOption(pkg.id)}
                            >
                              Add Option
                            </CustomButton>
                          </Box>

                          {/* Max Selections for Multi-select */}
                          {pkg.type === 'multi-select' && (
                            <TextField
                              fullWidth
                              type="number"
                              placeholder="Max selections (leave empty for unlimited)"
                              value={pkg.maxSelections || ''}
                              onChange={(e) => handlePackageChange(pkg.id, 'maxSelections', e.target.value)}
                              error={pkg.maxSelections !== '' && pkg.maxSelections !== undefined && parseFloat(pkg.maxSelections) < 0}
                              helperText={pkg.maxSelections !== '' && pkg.maxSelections !== undefined && parseFloat(pkg.maxSelections) < 0 ? 'Must be 0 or greater' : ''}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#f6f8f8',
                              borderRadius: '12px',
                              height: '56px',
                              '& fieldset': {
                                border: 'none'
                                  }
                                },
                                '& .MuiInputBase-input': {
                                  fontFamily: '"Inter", sans-serif',
                                  fontSize: '16px',
                                }
                              }}
                            />
                          )}
                        </Box>
                      )}

                      {/* Addons Items */}
                      {pkg.type === 'addons' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <CustomTypography variant="body1" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.text }}>
                            Ad-on Items:
                          </CustomTypography>
                          {pkg.items?.map((item, itemIndex) => (
                            <Box key={item.id} sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <TextField
                                  fullWidth
                                  placeholder="Item label"
                                  value={item.label}
                                  onChange={(e) => handleItemChange(pkg.id, item.id, 'label', e.target.value)}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: '#f6f8f8',
                                      borderRadius: '12px',
                                      height: '56px',
                                      '& fieldset': {
                                border: 'none'
                                      }
                                    },
                                    '& .MuiInputBase-input': {
                                      fontFamily: '"Inter", sans-serif',
                                      fontSize: '16px',
                                    }
                                  }}
                                />
                                {pkg.items && pkg.items.length > 1 && (
                                  <IconButton
                                    onClick={() => handleRemoveItem(pkg.id, item.id)}
                                    sx={{
                                      width: '40px',
                                      height: '40px',
                                      color: DESIGN_TOKENS.colors.primary,
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                  type="number"
                                  placeholder="Price per unit"
                                  value={item.pricePerUnit}
                                  onChange={(e) => handleItemChange(pkg.id, item.id, 'pricePerUnit', e.target.value)}
                                  error={item.pricePerUnit !== '' && parseFloat(item.pricePerUnit) < 0}
                                  helperText={item.pricePerUnit !== '' && parseFloat(item.pricePerUnit) < 0 ? 'Must be 0 or greater' : ''}
                                  sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: '#f6f8f8',
                                      borderRadius: '12px',
                                      height: '56px',
                                      '& fieldset': {
                                border: 'none'
                              }
                            },
                            '& .MuiInputBase-input': {
                              fontFamily: '"Inter", sans-serif',
                              fontSize: '16px',
                            }
                          }}
                        />
                        <TextField
                                  type="number"
                                  placeholder="Min units"
                                  value={item.minUnits}
                                  onChange={(e) => handleItemChange(pkg.id, item.id, 'minUnits', e.target.value)}
                                  error={item.minUnits !== '' && parseFloat(item.minUnits) < 0}
                                  helperText={item.minUnits !== '' && parseFloat(item.minUnits) < 0 ? 'Must be 0 or greater' : ''}
                          sx={{
                                    width: '120px',
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#f6f8f8',
                              borderRadius: '12px',
                              height: '56px',
                              '& fieldset': {
                                border: 'none'
                                      }
                                    },
                                    '& .MuiInputBase-input': {
                                      fontFamily: '"Inter", sans-serif',
                                      fontSize: '16px',
                                    }
                                  }}
                                />
                                <TextField
                                  type="number"
                                  placeholder="Max units"
                                  value={item.maxUnits}
                                  onChange={(e) => handleItemChange(pkg.id, item.id, 'maxUnits', e.target.value)}
                                  error={item.maxUnits !== '' && parseFloat(item.maxUnits) < 0}
                                  helperText={item.maxUnits !== '' && parseFloat(item.maxUnits) < 0 ? 'Must be 0 or greater' : ''}
                                  sx={{
                                    width: '120px',
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: '#f6f8f8',
                                      borderRadius: '12px',
                                      height: '56px',
                                      '& fieldset': {
                                border: 'none'
                                      }
                                    },
                                    '& .MuiInputBase-input': {
                                      fontFamily: '"Inter", sans-serif',
                                      fontSize: '16px',
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                          ))}
                          <Box sx={{ alignSelf: 'flex-start' }}>
                            <CustomButton
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddItem(pkg.id)}
                            >
                              Add Item
                            </CustomButton>
                          </Box>

                          {/* Max Customizable Items */}
                          <TextField
                            fullWidth
                            type="number"
                            placeholder="Max customizable items (leave empty for unlimited)"
                            value={pkg.maxCustomizableItems || ''}
                            onChange={(e) => handlePackageChange(pkg.id, 'maxCustomizableItems', e.target.value)}
                            error={pkg.maxCustomizableItems !== '' && pkg.maxCustomizableItems !== undefined && parseFloat(pkg.maxCustomizableItems) < 0}
                            helperText={pkg.maxCustomizableItems !== '' && pkg.maxCustomizableItems !== undefined && parseFloat(pkg.maxCustomizableItems) < 0 ? 'Must be 0 or greater' : ''}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#f6f8f8',
                                borderRadius: '12px',
                                height: '56px',
                                '& fieldset': {
                                border: 'none'
                              }
                            },
                            '& .MuiInputBase-input': {
                              fontFamily: '"Inter", sans-serif',
                              fontSize: '16px',
                            }
                          }}
                        />
                      </Box>
                      )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>

                  {/* Add Button - After all packages */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <CustomButton 
                      variant="primary" 
                      size="large"
                      startIcon={<AddIcon />}
                      onClick={handleAddPackage}
                    >
                      Add package
                    </CustomButton>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
  );
};

export default ProductDetail;

